// Which Supabase project the running bundle talks to, resolved from the
// build-time VITE_SUPABASE_URL (Vite bakes it in, so it never lies about the
// running code). Shared by the SupabaseEnvBadge and the dev/staging tooling
// gates below.

export const STAGING_REF = 'addhitwggmczehzyxcbc';
export const PROD_REF = 'qaqskfvvfjewmfmkdchq';

/** `https://<ref>.supabase.co` -> `<ref>`; null for anything else. */
export function extractProjectRef(url: string | undefined): string | null {
  if (!url) return null;
  const match = url.match(/^https?:\/\/([a-z0-9]+)\.supabase\.co/i);
  return match ? match[1] : null;
}

export function supabaseProjectRef(): string | null {
  return extractProjectRef(import.meta.env.VITE_SUPABASE_URL as string | undefined);
}

/** True when the bundle is pointed at the STAGING project. */
export function isStagingEnv(): boolean {
  return supabaseProjectRef() === STAGING_REF;
}

// Test hooks that must never fire against prod venues: MOCK_LAUNCH, the
// NextUpWidget "Test Volgende" / "Test Start" buttons, window.dispatchDebug and
// the fake PC tiles. Available in any dev server AND in staging builds
// (packaged "RealityOperator Staging" included), so the queue drill in
// STAGING.md can run on a tablet without a dev machine. A prod bundle can
// never enable them: the ref is baked in at build time.
export const DEV_TOOLS_ENABLED: boolean = !!import.meta.env.DEV || isStagingEnv();

/** localStorage keys the staging tools read. */
export const MOCK_LAUNCH_KEY = 'MOCK_LAUNCH';
export const FAKE_PC_TILES_KEY = 'FAKE_PC_TILES';
export const FAKE_RUN_SECONDS_KEY = 'FAKE_RUN_SECONDS';
export const FAKE_LOADING_SECONDS_KEY = 'FAKE_LOADING_SECONDS';
export const FAKE_RUN_SECONDS_DEFAULT = 60;
export const FAKE_LOADING_SECONDS_DEFAULT = 5;
/** Fired on window after a staging tool setting changed (same tab). */
export const DEV_TOOLS_CHANGED_EVENT = 'reality-operator:dev-tools-changed';

/** MOCK_LAUNCH: launch/kill calls short-circuit without contacting a PC agent. */
export function mockLaunchActive(): boolean {
  if (!DEV_TOOLS_ENABLED || typeof window === 'undefined') return false;
  try {
    return window.localStorage?.getItem(MOCK_LAUNCH_KEY) === '1';
  } catch {
    return false;
  }
}

/** FAKE_PC_TILES: room PCs without an ip_address render as <FakePCTile>
 *  (an in-memory fake agent per tile) instead of <PCTile>. Staging only. */
export function fakePcTilesActive(): boolean {
  if (!DEV_TOOLS_ENABLED || typeof window === 'undefined') return false;
  try {
    return window.localStorage?.getItem(FAKE_PC_TILES_KEY) === '1';
  } catch {
    return false;
  }
}

function readPositiveInt(key: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  try {
    const n = Number(window.localStorage?.getItem(key));
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
  } catch {
    return fallback;
  }
}

/** How long a fake tile shows "running" before it finishes (seconds). */
export function readFakeRunSeconds(): number {
  return readPositiveInt(FAKE_RUN_SECONDS_KEY, FAKE_RUN_SECONDS_DEFAULT);
}

/** How long a fake tile stays in "loading" after a launch (seconds). */
export function readFakeLoadingSeconds(): number {
  return readPositiveInt(FAKE_LOADING_SECONDS_KEY, FAKE_LOADING_SECONDS_DEFAULT);
}
